import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MatchRow from '@/components/feature/MatchRow';
import Pagination from '@/components/feature/Pagination';
import { useSEO } from '@/hooks/useSEO';
import { getLeague, getMatchesByLeague, getTeamsByLeague } from '@/utils/data';
import type { Match } from '@/types';

const statusOptions = [
  { value: 'all', label: '全部状态' },
  { value: 'finished', label: '已完赛' },
  { value: 'live', label: '进行中' },
  { value: 'upcoming', label: '未开始' },
];

const seasonOptions = ['全部赛季', '2026-27', '2025-26', '2024-25'];

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { id = 'all' } = useParams();
  const league = getLeague(id);
  const isAll = id === 'all';

  const title = isAll ? '全部赛事' : `${league?.shortName} 篮球赛事`;
  useSEO({
    title: isAll ? '全部篮球赛事 - 篮球比分' : `${league?.name || '赛事'}篮球赛事 - 篮球比分`,
    description: isAll
      ? '汇总国内外多项篮球赛事的最新赛程与比分信息，可按联赛、状态、球队多维度筛选。'
      : `查看${league?.name || ''}最新赛程、实时比分与比赛结果，掌握该联赛各支球队的近期表现。`,
    keywords: `篮球赛事,${league?.shortName || '篮球'}赛程,篮球比分,篮球排名`,
  });

  const baseMatches = getMatchesByLeague(id);
  const leagueTeams = isAll ? [] : getTeamsByLeague(id);

  const [season, setSeason] = useState('全部赛季');
  const [status, setStatus] = useState('all');
  const [teamId, setTeamId] = useState('all');
  const [sort, setSort] = useState<'time' | 'hot'>('time');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    let list: Match[] = [...baseMatches];
    if (season !== '全部赛季' && season !== '2026-27') list = [];
    if (status !== 'all') list = list.filter((m) => m.status === status);
    if (teamId !== 'all') list = list.filter((m) => m.homeTeamId === teamId || m.awayTeamId === teamId);
    list.sort((a, b) => (sort === 'hot' ? b.hot - a.hot : a.id.localeCompare(b.id)));
    return list;
  }, [baseMatches, season, status, teamId, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetAll = () => {
    setSeason('全部赛季');
    setStatus('all');
    setTeamId('all');
    setSort('time');
    setPage(1);
  };

  const update = (fn: () => void) => {
    fn();
    setPage(1);
  };

  return (
    <div className="px-4 md:px-6 py-6 animate-fade-up">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '赛事', to: '/category/all' },
          { label: title },
        ]}
      />

      <header className="mt-4 mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">{title}</h1>
        <p className="mt-2 text-sm md:text-[15px] text-foreground-600 max-w-3xl leading-relaxed">
          {isAll
            ? '汇总国内外多项篮球赛事的最新赛程与比分，支持按联赛、比赛状态与球队名称进行多维筛选。'
            : league?.intro}
        </p>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Link
            to="/category/all"
            className={`px-3 py-1.5 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap border ${
              isAll ? 'bg-primary-500 text-background-50 border-primary-500' : 'bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300'
            }`}
          >
            全部
          </Link>
          {['nba', 'cba', 'euro', 'wnba', 'ncaa', 'fiba'].map((lid) => {
            const l = getLeague(lid);
            if (!l) return null;
            return (
              <Link
                key={lid}
                to={`/category/${lid}`}
                className={`px-3 py-1.5 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap border ${
                  id === lid ? 'bg-primary-500 text-background-50 border-primary-500' : 'bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300'
                }`}
              >
                {l.shortName}
              </Link>
            );
          })}
        </div>
      </header>

      <section className="bg-background-50 border border-background-200/80 rounded-lg mb-6">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="w-full lg:hidden flex items-center justify-between px-4 py-3 cursor-pointer"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
            <span className="w-4 h-4 flex items-center justify-center text-primary-500"><i className="ri-filter-3-line"></i></span>
            筛选与排序
          </span>
          <i className={filtersOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
        </button>

        <div className={`${filtersOpen ? 'block' : 'hidden'} lg:block px-4 pb-4 lg:py-4 border-t lg:border-t-0 border-background-200/70`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <span className="block text-xs text-foreground-500 mb-1.5">赛季年份</span>
              <select
                value={season}
                onChange={(e) => update(() => setSeason(e.target.value))}
                className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                {seasonOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="block text-xs text-foreground-500 mb-1.5">比赛状态</span>
              <select
                value={status}
                onChange={(e) => update(() => setStatus(e.target.value))}
                className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                {statusOptions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="block text-xs text-foreground-500 mb-1.5">球队名称</span>
              <select
                value={teamId}
                onChange={(e) => update(() => setTeamId(e.target.value))}
                disabled={isAll}
                className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="all">全部球队</option>
                {leagueTeams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="block text-xs text-foreground-500 mb-1.5">排序方式</span>
              <select
                value={sort}
                onChange={(e) => update(() => setSort(e.target.value as 'time' | 'hot'))}
                className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="time">按时间排序</option>
                <option value="hot">按热度排序</option>
              </select>
            </label>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-xs text-foreground-500">
              共筛选出 <span className="text-primary-600 font-semibold">{filtered.length}</span> 场比赛
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="flex items-center gap-1 h-9 px-3 rounded-md border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        </div>
      </section>

      {pageItems.length > 0 ? (
        <>
          <div className="bg-background-50 border border-background-200/80 rounded-lg overflow-hidden">
            {pageItems.map((m) => (
              <MatchRow key={m.id} match={m} />
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="bg-background-50 border border-dashed border-background-300 rounded-lg py-16 flex flex-col items-center text-center px-4">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-search-eye-line text-2xl"></i>
          </span>
          <h2 className="font-heading font-bold text-lg text-foreground-900">暂无匹配的比赛</h2>
          <p className="mt-2 text-sm text-foreground-500 max-w-md">当前筛选条件下没有找到相关赛事，试试调整赛季年份、比赛状态或球队条件。</p>
          <button
            type="button"
            onClick={resetAll}
            className="mt-5 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            重置筛选条件
          </button>
        </div>
      )}
    </div>
  );
}